import type { TrackingEventName, TrackingProperties } from "../types";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/*
 * IP-1378 contract: the FTD Purchase carries the real EUR value of the deposit,
 * superseding the signal-only `value: 0` agreed in IP-1237.
 *
 * `eurAmount` is the backend's EUR reference value for the transaction, already
 * converted from the native fiat, crypto or stablecoin currency and snapshotted
 * at credit time. The native amount is never sent: Meta's `currency` accepts
 * ISO 4217 fiat codes only, so it is always 'EUR', and a native crypto figure
 * under a EUR label would destroy value optimisation.
 */
const FALLBACK_VALUE = 0;

/*
 * A missing or malformed `eurAmount` falls back to zero rather than dropping the
 * event. The original IP-1237 purpose, lookalike audience modelling, still works
 * at zero value, so losing the conversion signal entirely is the worse outcome.
 * Negative values are treated as malformed.
 */
const toPurchaseValue = (eurAmount: unknown) => {
  if (typeof eurAmount !== "string" && typeof eurAmount !== "number") {
    return FALLBACK_VALUE;
  }

  const parsed = Number(eurAmount);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return FALLBACK_VALUE;
  }

  return Number(parsed.toFixed(2));
};

let hasFiredPurchase = false;

// Products own the Meta Pixel script and only the first deposit is reported,
// as a Purchase (IP-1237).
const sendToMetaPixel = (
  event: TrackingEventName,
  payload: TrackingProperties,
) => {
  if (event !== "depositCompleteFirst") {
    return;
  }

  /*
   * A first deposit happens once per user lifetime, so never fire Purchase
   * more than once per page load — this guards against a poll batch carrying
   * two first-deposit-classified events and against the backend re-delivering
   * a pending event on a later polling cycle.
   */
  if (hasFiredPurchase) {
    return;
  }

  hasFiredPurchase = true;
  window.fbq?.("track", "Purchase", {
    value: toPurchaseValue(payload.eurAmount),
    currency: "EUR",
  });
};

export default sendToMetaPixel;
